import { ChangeEvent, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as R from 'ramda';

import { requestUserPasswordReset } from '@atom/actions/userActions';
import { Button, Icon, IconButton, Modal, TextField } from '@atom/mui';
import { ArrowBackIcon, EmailIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import accountUtilities from '@atom/utilities/accountUtilities';

import AuthLogo from './AuthLogo';

import classes from './login.module.css';

const styles = {
  dialog: {
    bodyStyle: {
      margin: 0,
      padding: '1.875rem 1.875rem 1rem 1.875rem',
    },
    contentStyle: {
      paddingLeft: '2rem',
      paddingRight: '2rem',
      paddingTop: '2rem',
    },
    title: {
      fontSize: fonts.xl,
      padding: '1.125em',
      fontWeight: '500',
    },
    actionsContainerStyle: {
      paddingTop: '1.5rem',
      paddingBottom: '1.125rem',
    },
  },
  logo: {
    width: '100px',
  },
  backButton: {
    color: colors.neutral.gray,
    padding: 0,
  },
  emailIcon: {
    color: colors.neutral.gray,
    marginTop: '0.125rem',
    paddingLeft: '1rem',
  },
  input: {
    border: 'none',
    height: '38px',
    lineHeight: '38px',
    fontSize: '14px',
    outline: 'none',
  },
};

interface ErrorsState {
  email: string;
}

const ResetPasswordModal = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorsState>({ email: '' });
  const [email, setEmail] = useState<string>('');

  const dispatch = useDispatch();

  const resetAndCloseModal = () => {
    setEmail('');
    setErrors({ email: '' });
    setOpen(false);
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const checkIsEmailValid = (): boolean => {
    if (!accountUtilities.isValidEmail(email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return false;
    }
    return true;
  };

  const submit = () => {
    if (checkIsEmailValid()) {
      dispatch(requestUserPasswordReset({ email }));
      resetAndCloseModal();
    }
  };

  const isSubmitDisabled = R.isNil(email) || R.isEmpty(email);

  return (
    <>
      <div
        onClick={() => setOpen(true)}
        className={classes.passwordResetLabelContainer}
      >
        Forgot password?
      </div>
      <Modal
        title=""
        open={open}
        style={styles.dialog.bodyStyle}
        width="sx"
        disableHeader
        disableFooter
        contentStyle={styles.dialog.contentStyle}
      >
        <div className={classes.passwordResetModalContainer}>
          <div className={classes.passwordResetLogoIconContainer}>
            <div className={classes.passwordResetBackIcon}>
              <IconButton
                onClick={resetAndCloseModal}
                iconStyle={styles.backButton}
                tooltip="Back"
              >
                <Icon icon={ArrowBackIcon} />
              </IconButton>
            </div>
            <div className={classes.passwordResetLogo}>
              <AuthLogo style={styles.logo} />
            </div>
          </div>
          <div className={classes.passwordResetHeading}>
            Reset your password
          </div>
          <div className={classes.passwordResetText}>
            Please enter your email address. We will send you an email to reset
            your password.
          </div>
          {errors.email && (
            <div className={classes.errorText}>{errors.email}</div>
          )}
          <div className={classes.passwordResetEmailContainer}>
            <Icon style={styles.emailIcon} icon={EmailIcon} />
            <div className={classes.passwordResetEmailInput}>
              <TextField
                variant="standard"
                name="email"
                value={email}
                placeholder="yours@example.com"
                onChange={onChange}
                slotProps={{ input: { disableUnderline: true } }}
              />
            </div>
          </div>
        </div>
        <div style={styles.dialog.actionsContainerStyle}>
          <Button
            color="primary"
            variant="contained"
            fullWidth
            disabled={isSubmitDisabled}
            onClick={submit}
          >
            Send email
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default ResetPasswordModal;
